import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Eye, EyeOff } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';

export const LoginScreen = () => {
  const { t, language, setLanguage } = useLanguage();
  const { login, sessionExpiredMessage } = useAuth();
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberName, setRememberName] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Pre-fill name from localStorage if previously saved
  useEffect(() => {
    const saved = localStorage.getItem('guestName');
    if (saved) {
      setName(saved);
      setRememberName(true);
    }
  }, []);

  // Warm up the serverless function on mount so it's ready before login is submitted
  useEffect(() => {
    api.get('/api/').catch(() => {});
  }, []);

  // Clear local form error when a session-expiry notice takes over
  useEffect(() => {
    if (sessionExpiredMessage) setError('');
  }, [sessionExpiredMessage]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(name, password);
      if (rememberName) {
        localStorage.setItem('guestName', name);
      } else {
        localStorage.removeItem('guestName');
      }
    } catch (err) {
      const status = err.response?.status;
      if (status === 401) {
        setError(t('loginErrorPassword'));
      } else if (status === 404) {
        setError(t('loginErrorName'));
      } else {
        setError(t('loginErrorGeneric'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex flex-col items-center justify-center px-6">
      {/* Language Switcher */}
      <div className="absolute top-6 right-6 flex items-center gap-1.5 text-sm">
        <button
          onClick={() => setLanguage('fr')}
          aria-label="Passer en français"
          aria-pressed={language === 'fr'}
          className={`px-3 py-1 rounded-full transition-all ${
            language === 'fr'
              ? 'bg-[#B5985A] text-white font-medium'
              : 'text-[#999] hover:text-[#2D2A26] hover:bg-[#F5F0E8]'
          }`}
        >
          FR
        </button>
        <button
          onClick={() => setLanguage('en')}
          aria-label="Switch to English"
          aria-pressed={language === 'en'}
          className={`px-3 py-1 rounded-full transition-all ${
            language === 'en'
              ? 'bg-[#B5985A] text-white font-medium'
              : 'text-[#999] hover:text-[#2D2A26] hover:bg-[#F5F0E8]'
          }`}
        >
          EN
        </button>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="w-full max-w-sm"
      >
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="font-serif text-5xl sm:text-6xl font-light text-[#2D2A26] mb-3">
            A & T
          </h1>
          <p className="text-[#999] text-sm tracking-[0.2em] uppercase">
            {language === 'en' ? (
              <>September 5<sup className="text-[0.65em]">th</sup>, 2026</>
            ) : (
              t('loginDate')
            )}
          </p>
        </div>

        {/* Session expiry notice */}
        {sessionExpiredMessage && (
          <motion.p
            role="alert"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-[#B5985A] text-sm text-center mb-6 border border-[#E8D9B5] bg-[#FBF6EC] rounded px-4 py-3"
          >
            {sessionExpiredMessage}
          </motion.p>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="login-name" className="block font-serif text-lg text-[#2D2A26] mb-2">
              {t('loginName')}
            </label>
            <input
              id="login-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('loginNamePlaceholder')}
              required
              className="w-full border-b border-[#DDD] py-3 px-1 text-[#2D2A26] placeholder-[#BBB] focus:outline-none focus:border-[#B5985A] transition-colors bg-transparent"
              data-testid="login-name-input"
            />
            <label className="flex items-center gap-2 mt-3 cursor-pointer select-none w-fit">
              <input
                type="checkbox"
                checked={rememberName}
                onChange={(e) => setRememberName(e.target.checked)}
                className="sr-only"
                data-testid="login-remember-checkbox"
              />
              <span className="w-4 h-4 border border-[#999] flex items-center justify-center flex-shrink-0">
                {rememberName && (
                  <svg viewBox="0 0 12 12" className="w-2.5 h-2.5" stroke="#999" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="1.5,6 4.5,9.5 10.5,2.5" />
                  </svg>
                )}
              </span>
              <span className="text-sm text-[#999]">{t('loginRememberName')}</span>
            </label>
          </div>

          <div>
            <label htmlFor="login-password" className="block font-serif text-lg text-[#2D2A26] mb-2">
              {t('loginPassword')}
            </label>
            <div className="relative">
              <input
                id="login-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full border-b border-[#DDD] py-3 px-1 pr-8 text-[#2D2A26] placeholder-[#BBB] focus:outline-none focus:border-[#B5985A] transition-colors bg-transparent"
                data-testid="login-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-1 bottom-3 text-[#BBB] hover:text-[#B5985A] transition-colors"
                aria-label={showPassword ? t('loginHidePassword') : t('loginShowPassword')}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {error && (
            <motion.p
              role="alert"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-[#C2734F] text-sm text-center"
            >
              {error}
            </motion.p>
          )}

          <div className="text-center pt-2">
            {(!name || !password) && (
              <span id="submit-hint" className="sr-only">
                {t('loginSubmitHint')}
              </span>
            )}
            <button
              type="submit"
              disabled={loading || !name || !password}
              aria-describedby={(!name || !password) ? 'submit-hint' : undefined}
              className="bg-[#B5985A] text-white px-10 py-3 rounded hover:bg-[#9A7F48] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="login-submit-button"
            >
              {loading ? t('loginLoading') : t('loginSubmit')}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};
